import React, { FC, useEffect, useState } from 'react'

import Select from 'react-select'

import { MAX_TEXT_LENGTH, MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from 'tabs/data/constants'
import { TrainingStatus } from 'tabs/settings/constants/trainingStatus'

import { cloneDeep, isEqual, noop } from 'lodash'
import * as S from './MetricsForm.style'
import { Prompt } from 'react-router-dom'
import { formatDateTime } from 'helpers/formatDate/formatDate'
import { DatasetRule, MetricTypes } from 'tabs/data/api/generated/models'
import { MetricsTypes } from 'tabs/data/constants/metricsTypes'
import { CreateDatasetMetricsParams } from 'tabs/data/api/metrics'
import { CreatableMultiselect } from 'tabs/data/components/CreatableMultiselect'
import { DeleteData } from 'tabs/data/components/DeleteData'
import { CButton } from 'tabs/data/components/CButton'
import { DatasetRules } from '../../DatasetRules'

const initMetricType = (type: MetricTypes): MetricTypes => {
  return MetricsTypes[type] || MetricTypes.Text
}

const MetricOptions: { value: MetricTypes; label: MetricTypes }[] = Object.values(MetricsTypes).map(value => ({
  value,
  label: value,
}))

interface MetricsFormProps {
  trainingStatus?: string
  name: string
  description: string
  updatedAt?: number

  cancelButtonLabel?: string
  formula: string
  rules: DatasetRule[]
  synonyms: string[]
  type: MetricTypes

  updateCallback?: () => Promise<void>
  onSubmit: (params: CreateDatasetMetricsParams) => Promise<void>
  onCancel?: () => void
  onDelete?: (forceDelete: boolean) => Promise<any>
}

export const MetricsForm: FC<MetricsFormProps> = ({
  trainingStatus,
  name = '',
  updatedAt,
  description = '',
  formula: formulaValue,
  rules: rulesValue,
  synonyms: synonymsValue,
  type: typeValue,
  cancelButtonLabel,

  updateCallback,
  onSubmit,
  onCancel = noop,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false)

  const [title, setTitle] = useState(name || '')
  const [text, setText] = useState(description || '')
  const [formula, setFormula] = useState(formulaValue || '')
  const [rules, setRules] = useState(rulesValue || [])
  const [synonyms, setSynonyms] = useState<string[]>(synonymsValue || [])
  const [type, setType] = useState(initMetricType(typeValue))

  const [errorTitle, setErrorTitle] = useState('')
  const [errorDescription, setErrorDescription] = useState('')
  const [errorFormula, setFormulaError] = useState('')
  const [errorRule, setRuleError] = useState('')

  const [errorAPI, setErrorAPI] = useState('')

  const formHasChanges =
    title.trim() !== (name || '').trim() ||
    text.trim() !== (description || '').trim() ||
    type !== typeValue ||
    formula.trim() !== (formulaValue || '').trim() ||
    !isEqual(rules, rulesValue || []) ||
    !isEqual(synonyms, synonymsValue || [])

  const formError = errorTitle || errorDescription || errorFormula || errorRule || errorAPI
  const requiredFieldEmpty = title.trim().length < MIN_TITLE_LENGTH || text.trim().length < 0

  const isSubmitButtonDisabled = requiredFieldEmpty || !formHasChanges || formError

  const handleTitleChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value = '' } = e.target

    if (value.length > MAX_TITLE_LENGTH) {
      setErrorTitle(`You've reached the max length for the text title value — ${MAX_TITLE_LENGTH} characters`)
    } else if (value.trim().length < MIN_TITLE_LENGTH) {
      setErrorTitle(`The title must be at least ${MIN_TITLE_LENGTH} characters long.`)
    } else {
      setErrorTitle('')
    }

    setTitle(value)
  }

  const handleDescriptionChange = e => {
    if (errorAPI) setErrorAPI('')

    const { value = '' } = e.target

    if (value.length > MAX_TEXT_LENGTH) {
      setErrorDescription(`You've reached the max length for the text value — ${MAX_TEXT_LENGTH} characters`)
    } else if (!value.trim().length) {
      setErrorDescription("The field can't be empty")
    } else {
      setErrorDescription('')
    }

    setText(value)
  }

  const handleFormulaChange = e => {
    if (errorAPI) setErrorAPI('')

    const isValidTextLength = e.target.value.length <= MAX_TEXT_LENGTH

    if (!isValidTextLength) {
      setFormulaError(`You've reached the max length for the text value — ${MAX_TEXT_LENGTH} characters`)
    } else {
      setFormulaError('')
    }

    setFormula(e.target.value)
  }

  const handleReset = () => {
    setTitle(name)
    setText(description)
    setFormula(formulaValue)
    setRules(cloneDeep(rulesValue || []))
    setSynonyms(cloneDeep(synonymsValue || []))
    setType(initMetricType(typeValue))

    setErrorAPI('')
    setErrorTitle('')
    setErrorDescription('')
    setFormulaError('')
    setRuleError('')

    if (onCancel) {
      onCancel()
    }
  }

  const handleSubmit = () => {
    if (loading) return

    try {
      setLoading(true)
      onSubmit({
        name: title.trim(),
        description: text.trim(),
        type,
        formula: formula.trim(),
        rules: (rules || []).filter(({ type, rule = '' }) => rule.trim().length && type),
        synonyms: (synonyms || []).filter(s => s.trim().length > 0),
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (formHasChanges) window.onbeforeunload = () => true
    if (!formHasChanges) window.onbeforeunload = () => null

    return () => {
      window.onbeforeunload = null
    }
  }, [formHasChanges, isSubmitButtonDisabled])

  return (
    <S.Container>
      <Prompt
        when={formHasChanges}
        message="Are you sure you want to leave this page without saving your current changes?"
      />
      <S.Form>
        <S.Row>
          <S.RowCell>
            <S.Title>Title*</S.Title>
            <S.InputContainer isError={Boolean(errorTitle)}>
              <S.TitleInput
                disabled={loading}
                onChange={handleTitleChange}
                value={title}
                placeholder="Enter title"
              />
            </S.InputContainer>
            <S.Error>{errorTitle}</S.Error>
          </S.RowCell>

          <S.RowCell>
            <S.Title>Type*</S.Title>
            <S.SelectorContainer>
              <Select
                isDisabled={loading}
                styles={S.selectCustomStyles}
                options={MetricOptions}
                value={MetricOptions.find(({ value }) => value === type)}
                onChange={item => {
                  setType(item.value)
                }}
              />
            </S.SelectorContainer>
          </S.RowCell>
        </S.Row>

        <S.Title>Description*</S.Title>
        <S.InputContainer isError={Boolean(errorDescription)}>
          <S.Textarea
            disabled={loading}
            placeholder="+ Insert text here"
            value={text}
            onChange={handleDescriptionChange}
            isError={errorDescription}
          />
        </S.InputContainer>
        <S.Error>{errorDescription}</S.Error>

        <S.Title>
          Synonyms <small>(optional)</small>
        </S.Title>
        <CreatableMultiselect disabled={loading} options={synonymsValue} values={synonyms} onChange={setSynonyms} />

        <S.Title>
          Formula <small>(optional)</small>
        </S.Title>
        <S.InputContainer isError={Boolean(errorFormula)}>
          <S.Textarea
            disabled={loading}
            placeholder="+ Insert text here"
            value={formula}
            onChange={handleFormulaChange}
            isError={Boolean(errorFormula)}
          />
        </S.InputContainer>
        <S.Error>{errorFormula}</S.Error>

        <DatasetRules disabled={loading} rules={rules} onChange={setRules} />

        <S.Footer>
          {updatedAt && (
            <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {formatDateTime(updatedAt * 1000)}</small>
          )}
          <CButton color="secondary" onClick={handleReset}>
            {cancelButtonLabel || 'Cancel'}
          </CButton>
          <CButton onClick={handleSubmit} disabled={Boolean(loading || isSubmitButtonDisabled)}>
            Save
          </CButton>
          {onDelete && (
            <DeleteData
              disabled={loading}
              buttonLabel="Delete Metric"
              subtitle={name}
              onDelete={onDelete}
              updateCallback={updateCallback}
            />
          )}
        </S.Footer>
      </S.Form>
      {trainingStatus === TrainingStatus.PENDING && <S.DisableScreen />}
    </S.Container>
  )
}
